import type { Address } from "viem"
import type { Config } from "wagmi"
import { readContract } from "@wagmi/core"
import { formatUnits } from "viem"

import { erc20Abi, venammAbi, veTokenAbi } from "@/domains/abis"
import { veInfo2Abi } from "@/domains/abis/ve-info2-abi"
import { LcAddress } from "@/domains/core"

import type { LockApiData } from "./types"

export type LockOnChainTransportAddresses = {
  veInfo2: LcAddress
  veToken: LcAddress
  governanceToken: Address
  vestedToken: Address
  venAmm: LcAddress | undefined
}

export class LockOnChainTransport {
  constructor(
    private config: Config,
    private readonly addresses: LockOnChainTransportAddresses,
    private readonly governanceTokenDecimals: number,
    private readonly vestedTokenDecimals: number,
  ) {}

  /**
   * Load lock info from on-chain using veInfo2 contract
   * Pure data loading - no state, no caching, no side effects
   */
  async loadLockInfo(userAddress: LcAddress): Promise<bigint[]> {
    if (!this.addresses.veInfo2) {
      return []
    }

    try {
      const result = await readContract(this.config, {
        address: this.addresses.veInfo2.toViemAddress(),
        abi: veInfo2Abi,
        functionName: "infoVe",
        args: [userAddress.toViemAddress()],
      })

      return result as bigint[]
    } catch (error) {
      console.error("Failed to load lock info from veInfo2:", error)
      return []
    }
  }

  /**
   * Load voting info from on-chain using veInfo2 contract
   * Pure data loading - no state, no caching, no side effects
   */
  async loadVotingInfo(): Promise<
    | {
        totalSupply: bigint
        gauges: string[]
        weights: bigint[]
        totalWeight: bigint
        epoch: bigint
      }
    | undefined
  > {
    if (!this.addresses.veInfo2) {
      return undefined
    }

    try {
      const result = await readContract(this.config, {
        address: this.addresses.veInfo2.toViemAddress(),
        abi: veInfo2Abi,
        functionName: "infoVoting",
        args: [],
      })

      const [totalSupply, gauges, weights, totalWeight, epoch] = result as unknown as [
        bigint,
        string[],
        bigint[],
        bigint,
        bigint,
      ]

      return {
        totalSupply,
        gauges,
        weights,
        totalWeight,
        epoch,
      }
    } catch (error) {
      console.error("Failed to load voting info from veInfo2:", error)
      return undefined
    }
  }

  /**
   * Load all user NFTs from on-chain using veInfo2 contract
   * Returns data in the same shape as the API response
   * Pure data loading - no state, no caching, no side effects
   */
  async loadUserNfts(userAddress: LcAddress): Promise<Array<LockApiData>> {
    if (!this.addresses.veInfo2) {
      return []
    }

    try {
      const values = await this.loadLockInfo(userAddress)

      if (!Array.isArray(values) || values.length === 0) {
        return []
      }

      const COUNT_PER_ITERATION = 7
      if (values.length % COUNT_PER_ITERATION !== 0) {
        console.error(`infoVe values array length must be a multiple of ${COUNT_PER_ITERATION}. Got ${values.length}`)
        return []
      }

      const nfts: Array<LockApiData> = []
      const nowUnixTs = Math.floor(Date.now() / 1000)

      // Process NFTs in chunks of 7 (id, lockValue, lockAmount, lockEnds, lastVoteTime, attachments, isVoting)
      for (let i = 0; i < values.length; i += COUNT_PER_ITERATION) {
        const [id, lockValue, lockAmount, lockEnds, lastVoteTime, attachments, isVoting] = values.slice(
          i,
          i + COUNT_PER_ITERATION,
        ) as [bigint, bigint, bigint, bigint, bigint, bigint, bigint]

        const nftId = Number(id)
        const lockEndsTimestamp = Number(lockEnds)
        const lastVoteTimeNumber = Number(lastVoteTime)
        const isVotingNumber = Number(isVoting)

        // Convert bigints to strings using formatUnits (matching API format)
        const lockAmountString = formatUnits(lockAmount, this.governanceTokenDecimals)
        const lockValueString = formatUnits(lockValue, this.vestedTokenDecimals)

        // Calculate derived fields using UTC timestamps (Unix timestamps are always UTC)
        // Compare timestamps directly to avoid timezone issues
        const hasExpired = lockEndsTimestamp > 0 ? lockEndsTimestamp < nowUnixTs : false

        // Check if max locked (25 weeks or more)
        // 25 weeks = 25 * 7 * 24 * 60 * 60 seconds = 15,120,000 seconds
        const SECONDS_IN_WEEK = 604800
        const maxLockedMinimumTimestamp = nowUnixTs + 25 * SECONDS_IN_WEEK
        const isMaxLocked = lockEndsTimestamp > 0 ? lockEndsTimestamp >= maxLockedMinimumTimestamp : false

        // isVoting means NFT is Not Reset = it is in use / has some votes running
        const isReset = isVotingNumber === 0
        const hasVotes = isVotingNumber > 0

        // hasVotedThisEpoch: check if voted in current epoch (current week)
        const hasVotedThisEpoch =
          hasVotes && (lastVoteTimeNumber > 0 ? lastVoteTimeNumber > nowUnixTs - (nowUnixTs % (86400 * 7)) : false)

        // needsResetToWithdraw: expired but not reset
        const needsResetToWithdraw = hasExpired && !isReset

        // canWithdraw: expired and reset
        const canWithdraw = hasExpired && isReset

        nfts.push({
          id: nftId,
          lockAmount: lockAmountString,
          lockAmountRaw: lockAmount, // Preserve raw BigInt from RPC for exact precision
          lockEnds: lockEndsTimestamp,
          lockValue: lockValueString,
          isMaxLocked,
          hasExpired,
          isReset,
          hasVotes,
          hasVotedThisEpoch,
          lastVoted: lastVoteTimeNumber,
          needsResetToWithdraw,
          canWithdraw,
        })
      }

      return nfts
    } catch (error) {
      console.error("Failed to load NFTs from on-chain:", error)
      return []
    }
  }

  /**
   * Get Venamm quote for a specific NFT ID
   * Pure data loading - no state, no caching, no side effects
   */
  async getVenammQuote(nftId: number): Promise<bigint | null> {
    if (!this.addresses.venAmm) {
      return null
    }

    try {
      const result = await readContract(this.config, {
        address: this.addresses.venAmm.toViemAddress(),
        abi: venammAbi,
        functionName: "getQuote",
        args: [BigInt(nftId)],
      })

      return result as bigint
    } catch (error) {
      console.error(`Failed to get Venamm quote for NFT ${nftId}:`, error)
      return null
    }
  }

  /**
   * Check if venAmm contract is approved to spend a specific NFT
   * Pure data loading - no state, no caching, no side effects
   */
  async checkVenammApproval(venAmmAddress: Address, veTokenAddress: Address, nftId: number): Promise<boolean> {
    try {
      const result = await readContract(this.config, {
        address: veTokenAddress,
        abi: veTokenAbi,
        functionName: "isApprovedOrOwner",
        args: [venAmmAddress, BigInt(nftId)],
      })

      return Boolean(result)
    } catch (error) {
      console.error(`Failed to check Venamm approval for NFT ${nftId}:`, error)
      return false
    }
  }

  /**
   * Get available token supply in venAmm contract
   * Pure data loading - no state, no caching, no side effects
   */
  async getVenammAvailableTokenSupply(): Promise<bigint | null> {
    if (!this.addresses.venAmm) {
      return null
    }

    try {
      const result = await readContract(this.config, {
        address: this.addresses.governanceToken,
        abi: erc20Abi,
        functionName: "balanceOf",
        args: [this.addresses.venAmm.toViemAddress()],
      })

      return result as bigint
    } catch (error) {
      console.error("Failed to get Venamm available token supply:", error)
      return null
    }
  }
}
